// The staff side of the API (§11.3). Same rules as `api.ts`: CSRF from /api/me, an
// Idempotency-Key kept by the caller for retries, nothing in local storage.

import {
  type Me,
  type StaffListQuery,
  StaffListSchema,
  type StaffPatchBody,
  StaffPatchResponseSchema,
  type StaffRaiseBodyInput,
  StaffRaiseResponseSchema,
  type StaffReplyBody,
  StaffReplyResponseSchema,
  StaffRequestDetailSchema,
  StaffStatsSchema,
} from "@hoffman-support/contracts";
import { ApiError } from "./api";

async function parse<T>(res: Response, parser: (v: unknown) => T): Promise<T> {
  if (!res.ok) {
    let failure = { status: res.status, code: "unknown", message: res.statusText };
    try {
      const body = (await res.json()) as {
        error?: { code?: string; message?: string; rules?: string[] };
        issues?: Array<{ path: string; message: string }>;
      };
      failure = {
        ...failure,
        code: body.error?.code ?? "unknown",
        message: body.error?.message ?? res.statusText,
        ...(body.issues ? { issues: body.issues } : {}),
        ...(body.error?.rules ? { rules: body.error.rules } : {}),
      };
    } catch {
      // Non-JSON failure body.
    }
    throw new ApiError(failure);
  }
  return parser(await res.json());
}

export function staffList(query: Partial<StaffListQuery>) {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query))
    if (v !== undefined && v !== "") params.set(k, String(v));
  return fetch(`/api/admin/requests?${params.toString()}`, { credentials: "same-origin" }).then(
    (r) => parse(r, (v) => StaffListSchema.parse(v)),
  );
}

export function staffStats() {
  return fetch("/api/admin/requests/stats", { credentials: "same-origin" }).then((r) =>
    parse(r, (v) => StaffStatsSchema.parse(v)),
  );
}

export function staffGet(id: string) {
  return fetch(`/api/admin/requests/${encodeURIComponent(id)}`, {
    credentials: "same-origin",
  }).then((r) => parse(r, (v) => StaffRequestDetailSchema.parse(v)));
}

function headers(me: Me, idempotencyKey?: string): Record<string, string> {
  return {
    "content-type": "application/json",
    "x-csrf-token": me.csrfToken,
    ...(idempotencyKey ? { "idempotency-key": idempotencyKey } : {}),
  };
}

export function staffPatch(me: Me, id: string, body: StaffPatchBody) {
  return fetch(`/api/admin/requests/${encodeURIComponent(id)}`, {
    method: "PATCH",
    credentials: "same-origin",
    headers: headers(me),
    body: JSON.stringify(body),
  }).then((r) => parse(r, (v) => StaffPatchResponseSchema.parse(v)));
}

export function staffReply(me: Me, idempotencyKey: string, id: string, body: StaffReplyBody) {
  return fetch(`/api/admin/requests/${encodeURIComponent(id)}/replies`, {
    method: "POST",
    credentials: "same-origin",
    headers: headers(me, idempotencyKey),
    body: JSON.stringify(body),
  }).then((r) => parse(r, (v) => StaffReplyResponseSchema.parse(v)));
}

export function staffRaise(me: Me, idempotencyKey: string, body: StaffRaiseBodyInput) {
  return fetch("/api/admin/requests", {
    method: "POST",
    credentials: "same-origin",
    headers: headers(me, idempotencyKey),
    body: JSON.stringify(body),
  }).then((r) => parse(r, (v) => StaffRaiseResponseSchema.parse(v)));
}
